import {useEffect, useMemo, useState} from 'react';

import {fetchSignedFileUrl} from 'llm/chat-library/ChatDAO';
import {isTextPreviewablePath} from 'llm/chat-library/shared/SkillFileUtils';
import {SkillFile} from 'llm/chat-library/tool-details/ManageSkillToolCallParser';
import sortedBy from 'util/sortedBy';

/**
 * Resolution state for a single skill-file entry rendered in the ManageSkill preview.
 *
 * - `inline`: content is literal text (or there is nothing to fetch); render as-is.
 * - `loading`: content is an artifact id whose text is being fetched.
 * - `resolved`: content is the fetched artifact text.
 * - `binary`: content lives in an artifact for a non-text file, so it is never fetched or shown.
 * - `error`: content is an artifact id that could not be resolved (no session / fetch failed).
 */
export type ResolvedSkillFileStatus = 'inline' | 'loading' | 'resolved' | 'binary' | 'error';

export interface ResolvedSkillFile extends SkillFile {
  status: ResolvedSkillFileStatus;
}

interface ArtifactContentState {
  status: 'loading' | 'resolved' | 'error';
  content: string;
}

/** Whether a file's content must be fetched from an artifact to preview it as text. */
function needsArtifactFetch(file: SkillFile): boolean {
  return file.artifactId != null && isTextPreviewablePath(file.path);
}

async function fetchArtifactText(chatSessionId: string, artifactId: string): Promise<string> {
  const signedUrl = await fetchSignedFileUrl(chatSessionId, artifactId);
  const response = await fetch(signedUrl);
  if (!response.ok) {
    throw new Error(`Failed to fetch artifact ${artifactId}: ${response.status}`);
  }
  return response.text();
}

/**
 * Resolve ManageSkill preview files whose content is provided by artifact id to their text, so the
 * preview shows the same name / description / SKILL.md body / file list it shows for inline content.
 *
 * Files with inline content (no `artifactId`) are returned unchanged. Artifact-id files with a text
 * extension are fetched from the chat session's uploaded files; while a fetch is in flight the file
 * is `loading`, and if no `chatSessionId` is available (or the fetch fails) it is `error`.
 * Artifact-id files that are not text (e.g. slide decks, images) are marked `binary` and never
 * fetched, since their bytes cannot be shown in the preview.
 */
export function useResolvedSkillFiles(
  files: Array<SkillFile>,
  chatSessionId?: string
): Array<ResolvedSkillFile> {
  const [contentById, setContentById] = useState<Record<string, ArtifactContentState>>({});

  // Stable key of the artifact ids to fetch, so the effect only re-runs when the set of ids
  // actually changes (files is a fresh array on every render, in arbitrary order).
  const artifactIdsKey = useMemo(() => {
    const ids = files.filter((file) => needsArtifactFetch(file)).map((file) => file.artifactId ?? '');
    return sortedBy(Array.from(new Set(ids)), [(id) => id]).join('|');
  }, [files]);

  useEffect(() => {
    if (!chatSessionId || artifactIdsKey.length === 0) {
      return undefined;
    }
    const sessionId = chatSessionId;
    let cancelled = false;
    const ids = artifactIdsKey.split('|');

    async function resolveArtifact(id: string): Promise<void> {
      setContentById((prev) => (prev[id] ? prev : {...prev, [id]: {status: 'loading', content: ''}}));
      try {
        const text = await fetchArtifactText(sessionId, id);
        if (!cancelled) {
          setContentById((prev) => ({...prev, [id]: {status: 'resolved', content: text}}));
        }
      } catch (error) {
        // Surface the failure inline so the preview stops spinning, then rethrow so a genuine
        // fetch/JS error still reaches the global error handler and reporting.
        if (!cancelled) {
          setContentById((prev) => ({...prev, [id]: {status: 'error', content: ''}}));
        }
        throw error;
      }
    }

    for (const id of ids) {
      resolveArtifact(id);
    }
    return () => {
      cancelled = true;
    };
  }, [chatSessionId, artifactIdsKey]);

  return files.map((file): ResolvedSkillFile => {
    if (file.artifactId == null) {
      return {...file, status: 'inline'};
    }
    if (!isTextPreviewablePath(file.path)) {
      return {...file, status: 'binary'};
    }
    if (!chatSessionId) {
      return {...file, status: 'error'};
    }
    const state = contentById[file.artifactId];
    if (!state || state.status === 'loading') {
      return {...file, status: 'loading'};
    }
    if (state.status === 'error') {
      return {...file, status: 'error'};
    }
    return {...file, content: state.content, status: 'resolved'};
  });
}
